import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { ReviewMessage } from '../outreachTypes';
import { isOpenPitch } from './reviewState';
import { hintLines } from './reviewHints';
import { reviewMessages as messages } from './reviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReviewQueueListProps {
  rows: ReviewMessage[];
  currentUuid: string | null;
  onPick: (uuid: string) => void;
  showAutopilotNote: boolean;
}

/**
 * The list of today's pitches (design outreach-v2 h1, left): the open one in colour, an approved or
 * skipped one dimmed with its chip, "Check" when the rules found something.
 */
const ReviewQueueListComponent: React.FC<ReviewQueueListProps> = ({ rows, currentUuid, onPick, showAutopilotNote }) => {
  const intl = useIntl();
  const items = useMemo(
    () =>
      rows.map((row) => {
        const open = isOpenPitch(row);
        const hasHint = hintLines(intl, row.lintHints, row.quality.problems).length > 0;
        const chip = !open
          ? row.status === 'skipped'
            ? { text: intl.formatMessage(messages.skipped), tone: shared.chipMuted }
            : { text: intl.formatMessage(messages.approved), tone: shared.chipPos }
          : hasHint
            ? { text: intl.formatMessage(messages.check), tone: shared.chipWarn }
            : null;
        return (
          <button
            key={row.messageUuid}
            type="button"
            className={clsx(styles.item, row.messageUuid === currentUuid && styles.itemOn, !open && styles.itemDone)}
            onClick={() => onPick(row.messageUuid)}
            disabled={!open}
            data-testid="review-item"
          >
            <span className="flex-grow-1 text-truncate">
              <b>{row.targetName ?? row.contactName}</b>
              <small>
                {row.step > 1
                  ? intl.formatMessage(messages.followUp)
                  : intl.formatMessage(messages.to, { name: row.contactName })}
              </small>
            </span>
            {chip !== null && <span className={clsx(shared.chip, chip.tone, styles.itemChip)}>{chip.text}</span>}
          </button>
        );
      }),
    [currentUuid, intl, onPick, rows]
  );
  return (
    <div className={styles.list} data-testid="review-list">
      {items}
      {showAutopilotNote && (
        <p className={`${shared.small} px-1 py-2 m-0`}>{intl.formatMessage(messages.autopilotNote)}</p>
      )}
    </div>
  );
};

export const ReviewQueueList = TypedMemo(ReviewQueueListComponent);
